import React, { useState, useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useAuth } from '../context/AuthContext';
import RoundStandings from '../components/RoundStandings';
import TeamStandings from '../components/TeamStandings';
import MatchCard from '../components/RoundPicker/MatchCard';
import ConsolidatedByeCard from '../components/RoundPicker/ConsolidatedByeCard';
import RoundHeader from '../components/RoundPicker/RoundHeader';
import SaveSection from '../components/RoundPicker/SaveSection';
import AdminViewingBanner from '../components/RoundPicker/AdminViewingBanner';
import AdminSaveDialog from '../components/RoundPicker/AdminSaveDialog';
import TeamMatchupsModal from '../components/RoundPicker/TeamMatchupsModal';
import NotificationPromptModal from '../components/NotificationPromptModal';
import { useNotifications } from '../hooks/useNotifications';
import { useMatchTimers } from '../hooks/useMatchTimers';
import { useRoundNavigation } from '../hooks/useRoundNavigation';
import { usePicksManager } from '../hooks/usePicksManager';
import { useScoresManager } from '../hooks/useScoresManager';
import { useAdminControls } from '../hooks/useAdminControls';
import { useRoundDataHeartbeat } from '../hooks/useRoundDataHeartbeat';
import { calculateRoundTotal, isByeMatch } from '../utils/matchHelpers';
import { userAPI } from '../services/api';
import './RoundPicker.css';

function RoundPicker() {
  const { user } = useAuth();
  const canSeePickCounts = user?.showPickCounts === true;
  const canSeeTeamMatchups = user?.showTeamMatchups === true;
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [teamMatchupsTeam, setTeamMatchupsTeam] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  // Custom hooks for state management
  const notifications = useNotifications();
  const [seasonId, setSeasonId] = useState(null);
  const admin = useAdminControls(user, seasonId);
  const roundNav = useRoundNavigation(admin.selectedUserId, admin.isViewingAsAdmin);
  const picks = usePicksManager(roundNav.matches, roundNav.currentRound);
  const scores = useScoresManager(roundNav.matches);
  const timers = useMatchTimers(roundNav.matches);
  const heartbeat = useRoundDataHeartbeat(roundNav.currentRound, !admin.isViewingAsAdmin && !scores.adminMode);

  // Update season ID when round navigation loads it
  useEffect(() => {
    if (roundNav.currentSeasonId) {
      setSeasonId(roundNav.currentSeasonId);
    }
  }, [roundNav.currentSeasonId]);

  // Auto-refresh when round data cache changes (detected by heartbeat)
  useEffect(() => {
    if (heartbeat.shouldRefresh) {
      console.log('[RoundPicker] Cache change detected, refreshing round data');
      roundNav.loadRoundData();
      roundNav.refreshStandings();
      heartbeat.resetRefreshFlag();
      notifications.showSuccess('New data available - page refreshed');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heartbeat.shouldRefresh]);

  // Mobile detection for swipe gestures
  const isMobile = window.innerWidth <= 767;

  // Configure swipe handlers for mobile navigation
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      // Swipe left = go to next round (if not at max)
      if (!scores.adminMode && !admin.isViewingAsAdmin && roundNav.currentRound < roundNav.maxRound) {
        roundNav.goToNextRound();
      }
    },
    onSwipedRight: () => {
      // Swipe right = go to previous round (if not at first)
      if (!scores.adminMode && !admin.isViewingAsAdmin && roundNav.currentRound > 1) {
        roundNav.goToPreviousRound();
      }
    },
    preventScrollOnSwipe: true,
    trackMouse: false, // Touch only, not mouse drag
    delta: 50, // Minimum swipe distance in pixels
  });

  // Fetch user profile to check notification prompt status
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user) return;

      try {
        const response = await userAPI.getProfile();
        setUserProfile(response.data.user);
      } catch (err) {
        console.error('Failed to load user profile:', err);
      }
    };

    loadUserProfile();
  }, [user]);

  // Check if notification modal should be shown
  useEffect(() => {
    const checkShouldShowModal = async () => {
      if (!userProfile || roundNav.loading) return;

      // Don't show if user has already seen it
      if (userProfile.has_seen_notification_prompt) return;

      // Don't show if user has visited settings
      if (userProfile.has_visited_settings) return;

      // Check if within 10-day window
      if (userProfile.notification_prompt_first_shown) {
        const firstShown = new Date(userProfile.notification_prompt_first_shown);
        const tenDaysAgo = new Date();
        tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);

        // If first shown more than 10 days ago, don't show
        if (firstShown < tenDaysAgo) return;
      }

      // Show the modal
      setShowNotificationModal(true);

      // Record that we're showing it (only if not recorded before)
      if (!userProfile.notification_prompt_first_shown) {
        try {
          await userAPI.recordNotificationPromptShown();
        } catch (err) {
          console.error('Failed to record notification prompt shown:', err);
        }
      }
    };

    checkShouldShowModal();
  }, [userProfile, roundNav.loading]);

  // Handler for marking the notification prompt as seen
  const handleMarkNotificationPromptSeen = async () => {
    try {
      await userAPI.markNotificationPromptSeen();
      setShowNotificationModal(false);
    } catch (err) {
      console.error('Failed to mark notification prompt as seen:', err);
    }
  };

  // Handle pick change - always update local state; admin saves are batched
  const handlePickChange = (matchId, teamId) => {
    picks.handlePickChange(matchId, teamId);
    if (!admin.isViewingAsAdmin) {
      notifications.setSuccess('');
    }
  };

  // Handle save all picks
  const handleSaveAllPicks = async () => {
    try {
      notifications.clearNotifications();
      await picks.saveAllPicks();
      notifications.showSuccess('All picks saved successfully!');

      // Pause heartbeat monitoring to avoid false "New data available" notification
      // The save will invalidate the cache, but we don't want to notify the user
      heartbeat.pauseMonitoring(60000); // Pause for 60 seconds

      // Refresh standings to update pick completion status (checkmark vs X)
      roundNav.refreshStandings();
    } catch (err) {
      notifications.setError(
        err.response?.data?.error || 'Failed to save picks. Please try again.'
      );
      await roundNav.loadRoundData();
    }
  };

  // Handle save all scores
  const handleSaveAllScores = async () => {
    try {
      notifications.clearNotifications();
      const result = await scores.saveAllScores(roundNav.matches);

      const { summary, results } = result;
      if (summary.failed > 0) {
        notifications.showSuccess(
          `Scores saved: ${summary.successful} successful, ${summary.failed} failed. Check console for details.`
        );
        console.warn('Batch score update results:', results);
      } else {
        notifications.showSuccess(
          `All scores saved successfully! ${summary.successful} match(es) updated.`
        );
      }

      // Optimistic update: Update match statuses and scores
      roundNav.setMatches((prevMatches) =>
        prevMatches.map((match) => {
          const scoreUpdate = result.completeScores.find((s) => s.matchId === match.id);
          if (scoreUpdate) {
            return {
              ...match,
              homeScore: scoreUpdate.homeScore,
              awayScore: scoreUpdate.awayScore,
              status: 'completed',
            };
          }
          return match;
        })
      );

      // Pause heartbeat monitoring to avoid false "New data available" notification
      heartbeat.pauseMonitoring(60000);

      // Trigger standings refresh
      roundNav.refreshStandings();
    } catch (err) {
      notifications.setError(
        err.message || err.response?.data?.error || 'Failed to save scores. Please try again.'
      );
      await roundNav.loadRoundData();
    }
  };

  // Handle status change
  const handleStatusChange = async (matchId, newStatus) => {
    try {
      await scores.handleStatusChange(matchId, newStatus);
      notifications.showSuccess('Match status updated', 2000);

      // Optimistic update
      roundNav.setMatches((prevMatches) =>
        prevMatches.map((match) =>
          match.id === matchId ? { ...match, status: newStatus } : match
        )
      );

      // Refresh standings if match completed
      if (newStatus === 'completed' || newStatus === 'awaiting_scores') {
        heartbeat.pauseMonitoring(60000);
        roundNav.refreshStandings();
      }
    } catch (err) {
      notifications.setError('Failed to update match status');
      console.error(err);
      await roundNav.loadRoundData();
    }
  };

  // Handle batch admin save confirmation
  const handleConfirmAdminSave = async () => {
    try {
      const result = await admin.batchSaveAdminPicks(
        admin.selectedUserId,
        picks.picks,
        picks.originalPicks,
        roundNav.matches,
        admin.saveReason
      );

      if (result.failed > 0) {
        notifications.showSuccess(
          `${result.saved} pick(s) saved, ${result.failed} failed. Check console for details.`
        );
        console.warn('Batch admin pick save results:', result.picks);
      } else {
        notifications.showSuccess(`${result.saved} pick(s) saved successfully`);
      }

      // Advance originalPicks to match current picks (clear dirty state)
      picks.setOriginalPicks({ ...picks.picks });

      admin.closeSaveDialog();

      // Pause heartbeat monitoring to avoid false "New data available" notification
      heartbeat.pauseMonitoring(60000);

      // Refresh standings to update pick completion status for the edited user
      roundNav.refreshStandings();
    } catch (err) {
      notifications.setError(
        err.response?.data?.error || 'Failed to save picks'
      );
      await roundNav.loadRoundData();
    }
  };

  // Loading state
  if (roundNav.loading) {
    return <div className="loading">Loading round {roundNav.currentRound}...</div>;
  }

  // Calculate points for display
  const totalPoints = calculateRoundTotal(roundNav.matches);

  // Get selected user for admin viewing
  const selectedUser = admin.allUsers.find((u) => u.id === admin.selectedUserId);

  // Count changed picks (for admin batch save display)
  const changedPicksCount = Object.entries(picks.picks).filter(
    ([matchId, teamId]) => picks.originalPicks[parseInt(matchId, 10)] !== teamId
  ).length;

  return (
    <div className="round-picker">
      {/* Notifications */}
      {notifications.error && <div className="error-message">{notifications.error}</div>}
      {notifications.success && (
        <div className="success-message">{notifications.success}</div>
      )}

      {/* Team Matchups Modal */}
      {teamMatchupsTeam && (
        <TeamMatchupsModal
          team={teamMatchupsTeam}
          onClose={() => setTeamMatchupsTeam(null)}
        />
      )}

      {/* Notification Prompt Modal */}
      {showNotificationModal && (
        <NotificationPromptModal
          onClose={() => setShowNotificationModal(false)}
          onMarkSeen={handleMarkNotificationPromptSeen}
        />
      )}

      <div {...(isMobile ? swipeHandlers : {})} className="picks-and-standings-container">
        {/* Team Ladder Sidebar - Left */}
        <TeamStandings refreshKey={roundNav.standingsRefreshKey} />

        <div className="matches-container">
          {/* Header */}
          <RoundHeader
            currentRound={roundNav.currentRound}
            roundInfo={roundNav.roundInfo}
            maxRound={roundNav.maxRound}
            matches={roundNav.matches}
            points={totalPoints}
            adminMode={scores.adminMode}
            isAdmin={admin.isAdmin}
            allUsers={admin.allUsers}
            selectedUserId={admin.selectedUserId}
            currentUserId={user.id}
            onPreviousRound={roundNav.goToPreviousRound}
            onNextRound={roundNav.goToNextRound}
            onToggleAdminMode={() => scores.setAdminMode(!scores.adminMode)}
            onUserSelect={admin.handleUserSelect}
          />

          {/* Admin viewing banner */}
          {admin.isViewingAsAdmin && admin.selectedUserId && (
            <AdminViewingBanner
              selectedUser={selectedUser}
              onReturn={admin.returnToOwnPicks}
            />
          )}

          {/* Matches list */}
          {roundNav.matches.length === 0 ? (
            <div className="no-matches">
              <p>No matches found for Round {roundNav.currentRound}</p>
            </div>
          ) : (
            <>
              {/* Regular matches */}
              {roundNav.matches
                .filter((match) => !isByeMatch(match))
                .map((match) => (
                  <MatchCard
                    key={match.id}
                    match={match}
                    picks={picks.picks}
                    adminMode={scores.adminMode}
                    isViewingAsAdmin={admin.isViewingAsAdmin}
                    editableScores={scores.editableScores}
                    timeUntilLock={timers}
                    isSuperAdmin={canSeePickCounts}
                    showTeamMatchups={canSeeTeamMatchups}
                    onTeamMatchupClick={setTeamMatchupsTeam}
                    onPickChange={handlePickChange}
                    onScoreChange={(matchId, scoreType, value) => {
                      scores.handleScoreChange(matchId, scoreType, value);
                      notifications.setSuccess('');
                    }}
                    onStatusChange={handleStatusChange}
                  />
                ))}

              {/* Consolidated bye matches */}
              {roundNav.matches.filter((match) => isByeMatch(match)).length > 0 && (
                <ConsolidatedByeCard
                  byeMatches={roundNav.matches.filter((match) => isByeMatch(match))}
                  adminMode={scores.adminMode}
                  onStatusChange={handleStatusChange}
                />
              )}
            </>
          )}
        </div>

        {/* User Standings Sidebar - Right */}
        <RoundStandings
          currentRound={roundNav.currentRound}
          currentUserId={user.id}
          refreshKey={roundNav.standingsRefreshKey}
          isFinalRound={roundNav.currentRound === roundNav.maxRound}
          allMatchesCompleted={roundNav.matches.every((m) => m.status === 'completed')}
          isAdmin={admin.isAdmin}
          onUserSelect={admin.handleUserSelect}
        />
      </div>

      {/* Save section */}
      {roundNav.matches.length > 0 &&
        !scores.adminMode &&
        picks.hasPickChanges() && (
          <SaveSection
            adminMode={false}
            saving={picks.saving || admin.adminSaving}
            onSave={admin.isViewingAsAdmin ? admin.openSaveDialog : handleSaveAllPicks}
            picksCount={admin.isViewingAsAdmin ? changedPicksCount : Object.keys(picks.picks).length}
            scoresCount={Object.keys(scores.editableScores).length}
            editableScores={scores.editableScores}
          />
        )}

      {roundNav.matches.length > 0 &&
        scores.adminMode &&
        scores.hasScoreChanges() && (
          <SaveSection
            adminMode={true}
            saving={scores.saving}
            onSave={handleSaveAllScores}
            picksCount={Object.keys(picks.picks).length}
            scoresCount={Object.keys(scores.editableScores).length}
            editableScores={scores.editableScores}
          />
        )}

      {/* Batch admin save dialog */}
      <AdminSaveDialog
        show={admin.showSaveDialog}
        selectedUser={selectedUser}
        saveReason={admin.saveReason}
        saving={admin.adminSaving}
        changedCount={changedPicksCount}
        onReasonChange={admin.setSaveReason}
        onConfirm={handleConfirmAdminSave}
        onCancel={admin.closeSaveDialog}
      />
    </div>
  );
}

export default RoundPicker;
